// src/services/fxService.ts
import apiClient from "./apiClient";
import { getApiErrorMessage } from "../utils/apiError";
import { FxRate, FxTransactionsApiResponse } from "../types/fx";

const RATE_PATH = "rate";

export interface FxRateHistoryResult {
  results: FxRate[];
  pagination: {
    totalItems?: number;
    currentPage?: number;
    totalPages?: number;
    limit?: number;
  };
}

export const getRateHistory = async (
  from: string,
  to: string,
  page = 1,
  limit = 10
): Promise<FxRateHistoryResult> => {
  try {
    const response = await apiClient.get(`${RATE_PATH}/history`, {
      params: { fromCurrency: from, toCurrency: to, page, limit },
    });
    const body = response.data as {
      data?: FxRate[];
      results?: FxRate[];
      pagination?: FxRateHistoryResult["pagination"];
    };

    return {
      results: body.data ?? body.results ?? [],
      pagination: body.pagination ?? {},
    };
  } catch (error) {
    console.error("Failed to fetch FX rate history:", error);
    throw new Error(getApiErrorMessage(error, "Failed to fetch FX rate history"));
  }
};

export const createRate = async (rateData: {
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}): Promise<FxRate> => {
  try {
    const response = await apiClient.post<FxRate>(`${RATE_PATH}/create`, rateData);
    return response.data;
  } catch (error) {
    console.error("Failed to create FX rate:", error);
    throw new Error(getApiErrorMessage(error, "Failed to create FX rate"));
  }
};

export const updateRate = async (rateData: {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}): Promise<FxRate> => {
  try {
    const { id: _id, ...updatePayload } = rateData;
    const response = await apiClient.patch<FxRate>(
      `${RATE_PATH}/update`,
      updatePayload
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to update FX rate for id ${rateData.id}:`, error);
    throw new Error(getApiErrorMessage(error, "Failed to update FX rate"));
  }
};

export const getFxTransactions = async (
  page = 1,
  limit = 10,
  startDate?: string,
  endDate?: string,
  search?: string
): Promise<FxTransactionsApiResponse> => {
  try {
    const params: Record<string, string | number> = { page, limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (search) params.search = search;

    const response = await apiClient.get(`${RATE_PATH}/all-exchanges`, {
      params,
    });
    const body = response.data as Record<string, unknown>;
    const results = (body?.results ?? body?.data ?? []) as FxTransactionsApiResponse["results"];
    const pagination = body?.pagination as FxTransactionsApiResponse["pagination"];
    return {
      success: true,
      results,
      pagination,
    };
  } catch (error) {
    console.error("Failed to fetch FX transactions:", error);
    throw new Error(getApiErrorMessage(error, "Failed to fetch FX transactions"));
  }
};

export const downloadFxTransactionsPDF = async (): Promise<Blob> => {
  try {
    const response = await apiClient.get(`${RATE_PATH}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error("Failed to download FX transactions PDF:", error);
    throw new Error(getApiErrorMessage(error, "Failed to download PDF"));
  }
};
